/**
 * CreatorStart (/start) — the creator on-ramp.
 *
 * Oct 2026 (Randy, fan-first pass). "Create a silo" used to mean: sign up,
 * then land in the Fan Portal — a social page full of other people's silos —
 * and hunt for the Become-a-Creator pill. People wrote in confused. This page
 * is what that button means now: you made an account, here are the two ways to
 * start, pick one.
 *
 * Both ways are first-class on purpose. Some people know on day one that they
 * want in, and making them walk through a trial they didn't ask for is its own
 * kind of friction.
 *
 *   • Subscribe now  → the existing platform checkout, which returns to
 *     Account & Billing and runs the welcome/BYOK explainer exactly as it
 *     always has.
 *   • 14 days free   → the existing trial checkout, which now returns HERE
 *     (?trial=started) so a trial starter gets the same explainer instead of
 *     being dropped into a dashboard with a dollar of credit and no idea the
 *     AI needs funding at all.
 *   • Or look around first → the Fan Portal, no judgement.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, Crown, CheckCircle, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser } from "@/lib/api";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";
import { refreshTrial, useTrial } from "@/lib/trial";
import TrialStartModal from "@/components/TrialStartModal";
import { FinishSetupModal } from "@/components/FinishSetupPill";

const LQIP = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDABQODxIPDRQSEBIXFRQYHjIhHhwcHj0sLiQySUBMS0dARkVQWnNiUFVtVkVGZIhlbXd7gYKBTmCNl4x9lnN+gXz/2wBDARUXFx4aHjshITt8U0ZTfHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHz/wAARCAAQABgDASIAAhEBAxEB/8QAFwAAAwEAAAAAAAAAAAAAAAAAAAQGBf/EACEQAAICAQMFAQAAAAAAAAAAAAECAAMEBRExEhMhUYFh/8QAFQEBAQAAAAAAAAAAAAAAAAAAAgP/xAAbEQADAAIDAAAAAAAAAAAAAAAAAQIDIRIxQf/aAAwDAQACEQMRAD8AVpWkEbuo+iaWNTQxALLJNLO3sT5+xunURUwZQCf2G6prTHKldoqb6sallBdAzcD3CSObqFt+QlwIHTwBxCSU5Gt0U5QvD//Z";

const Backdrop = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden" aria-hidden="true" data-testid="start-sunrise-backdrop">
    <div className="absolute inset-0 bg-[#0A0D0F]" />
    <div className="absolute inset-0 bg-cover bg-center scale-105 blur-xl" style={{ backgroundImage: `url(${LQIP})` }} />
    <img
      src="/brand/signup-sunrise-1536.jpg"
      srcSet="/brand/signup-sunrise-960.webp 960w, /brand/signup-sunrise-1536.webp 1536w"
      sizes="100vw" alt="" decoding="async"
      className="absolute inset-0 w-full h-full object-cover object-[50%_42%] brightness-[0.84] saturate-[1.06]"
    />
    <div className="absolute inset-0 bg-gradient-to-b from-[#04070E]/62 via-[#050810]/16 to-[#07050A]/74" />
    <div className="absolute right-[6%] top-[18%] w-[50vw] h-[50vw] rounded-full bg-[#FFB347]/10 blur-[120px]" />
  </div>
);

const GLASS = "rounded-2xl p-7 border border-[#F5F1E8]/14 bg-[#060A12]/22 backdrop-blur-2xl backdrop-brightness-[0.44] backdrop-saturate-150 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75)]";

export default function CreatorStart() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const pricing = usePublicPricing();
  const { trial } = useTrial();
  const user = getUser();
  const trialReturn = params.get("trial") === "started";

  const [busy, setBusy] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [welcome, setWelcome] = useState(false);
  const [activating, setActivating] = useState(trialReturn);
  const polls = useRef(0);

  const fee = fmt$(pricing.creator_monthly_fee_cents ?? pricing.platform_fee_cents);
  const days = trial?.trial_days || 14;

  // Routing-only acknowledgement: once they've been here, /dashboard stops
  // diverting them and resolves normally.
  const ack = useCallback(async () => {
    try {
      const r = await api.post("/me/creator-start-ack");
      const u = getUser();
      if (u) setUser({ ...u, creator_start_ack_at: r.data?.creator_start_ack_at });
    } catch { /* routing nicety — never block the actual action */ }
  }, []);

  // Trial return — the subscription is created at Stripe, but access flips on
  // the webhook. Poll until it lands instead of showing a Fan Portal for the
  // two seconds in between.
  useEffect(() => {
    if (!trialReturn) return;
    let cancel = false;
    const tick = async () => {
      if (cancel) return;
      polls.current += 1;
      try {
        const [t, me] = await Promise.all([refreshTrial(), api.get("/auth/me")]);
        if (cancel) return;
        setUser(me.data);
        if (t?.live || me.data?.has_platform_access) {
          setActivating(false);
          setWelcome(true);
          await ack();
          return;
        }
      } catch { /* keep waiting */ }
      if (polls.current >= 10) {
        setActivating(false);
        toast("Your trial is still activating — refresh in a moment.", { icon: "⏳" });
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialReturn]);

  // Already a creator and not mid-activation? Nothing to choose here.
  useEffect(() => {
    if (trialReturn || welcome || activating) return;
    if (user?.has_platform_access) nav("/dashboard", { replace: true });
  }, [user, trialReturn, welcome, activating, nav]);

  const subscribe = async () => {
    setBusy(true);
    await ack();
    try {
      const r = await api.post("/stripe/platform/checkout", { origin_url: window.location.origin, return_path: "/start" });
      if (r.data?.already_granted) {
        toast.success(r.data.message || "Access granted — no charge required.");
        nav("/dashboard");
        return;
      }
      window.location.href = r.data.url;
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.data?.detail || "Could not open checkout.");
    }
  };

  const openTrial = async () => { await ack(); setTrialOpen(true); };

  if (activating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-[#F5F1E8] relative isolate px-6" data-testid="creator-start-activating">
        <Backdrop />
        <SpinnerGap size={28} className="animate-spin text-[#FFC24A]" />
        <p className="mt-4 font-serif-display text-2xl text-center">Opening your {days} days…</p>
        <p className="mt-2 text-sm text-[#E7E2D6]/70 text-center max-w-sm">
          Stripe has your card on file and nothing has been charged. This takes a few seconds.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col text-[#F5F1E8] relative isolate" data-testid="creator-start-page">
      <Backdrop />

      <nav className="border-b border-[#F5F1E8]/10 bg-[#070B11]/70 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFC24A] to-[#E07B2C] text-[#1A1206] flex items-center justify-center shadow-[0_0_24px_rgba(255,194,74,0.35)]">
              <Sparkle size={18} weight="duotone" />
            </div>
            <span className="font-serif-display text-xl">My Silo</span>
          </Link>
          <Link
            to="/fan-portal"
            onClick={ack}
            data-testid="creator-start-browse-link"
            className="text-sm text-[#E7E2D6]/70 hover:text-[#FFC24A] transition-colors"
          >
            Look around first →
          </Link>
        </div>
      </nav>

      <main className="siloart-copy flex-1 max-w-5xl w-full mx-auto px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFC24A]/85 flex items-center gap-2">
            <Sparkle size={14} weight="duotone" /> Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-4 leading-tight max-w-3xl">
            Your account is ready. <em className="text-[#FFC24A] not-italic">Now pick how you start.</em>
          </h1>
          <p className="mt-4 text-[#E7E2D6]/80 max-w-2xl leading-relaxed">
            Both doors open the same Creator Dashboard — the Workbench, your silos, your public page,
            fan subscriptions and tips. The only difference is when the first payment happens.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 gap-5">
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.5 }}
            className={`${GLASS} md:max-w-xl`} data-testid="creator-start-subscribe-card"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FFC24A]">
              <Crown size={13} weight="duotone" /> Straight in
            </div>
            <div className="flex items-baseline gap-2 mt-3 flex-wrap">
              <span className="font-serif-display text-3xl">Subscribe now</span>
              <span className="text-xl font-medium text-[#FFC24A]" data-testid="creator-start-fee">{fee}</span>
              <span className="text-sm text-[#E7E2D6]/70">/month</span>
            </div>
            <p className="mt-2 text-sm text-[#E7E2D6]/75 leading-relaxed">
              When you know, you know. Lock in now! Up to {pricing.creator_tier_silo_cap} silos.
            </p>
            <button
              type="button" onClick={subscribe} disabled={busy}
              data-testid="creator-start-subscribe-btn"
              className="mt-6 w-full px-5 py-3 rounded-md bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A] transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_40px_-8px_rgba(255,194,74,0.45)]"
            >
              {busy ? "Opening Stripe…" : <>Subscribe and start building <ArrowRight size={15} /></>}
            </button>
          </motion.div>

          {/* Oct 2026 (Randy) — the trial is a PILL here, not a rival card.
              A full card beside "Subscribe now" argues against the very path
              this page exists to serve. Same treatment as the marketing page:
              a small button that explains itself when tapped. */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.5 }}
            className="flex flex-wrap items-center gap-3"
            data-testid="creator-start-trial-card"
          >
            <button
              type="button" onClick={openTrial}
              data-testid="creator-start-trial-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#A8C0A2]/45 bg-[#A8C0A2]/[0.10] text-[#E7E2D6] text-xs font-medium hover:bg-[#A8C0A2]/20 hover:border-[#A8C0A2]/80 hover:text-[#F5F1E8] transition-colors backdrop-blur-sm"
            >
              <Sparkle size={13} weight="duotone" /> {days}-day trial
            </button>
            <span className="text-[11px] text-[#E7E2D6]/55">Not sure yet? Try everything first.</span>
          </motion.div>
        </div>

        <div className="mt-8 rounded-2xl p-6 border border-[#FFC24A]/18 bg-[#05080C]/30 backdrop-blur-2xl backdrop-brightness-[0.34] text-sm text-[#E7E2D6]/80 leading-relaxed max-w-3xl" data-testid="creator-start-funding-note">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#FFC24A]/80 mb-2">
            <CheckCircle size={13} weight="duotone" /> One thing either way
          </div>
          AI answers cost pennies to generate, so a silo runs on either your own provider key — a
          one-minute setup, a “direct to the source, no middleman connection” we help you set up —
          or platform credit you top up. Either way: My-Silo is highly optimized. Even a single
          dollar could fuel your Silo for 30&ndash;50 queries. (Range varies, using site defaults.)
        </div>
      </main>

      <TrialStartModal open={trialOpen} onOpenChange={setTrialOpen} />
      {welcome && (
        <FinishSetupModal
          trial
          onClose={() => { setWelcome(false); nav("/dashboard"); }}
          onStart={() => { setWelcome(false); nav("/dashboard/account-settings?focus=byok"); }}
        />
      )}
    </div>
  );
}
